import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageCircleHeart, X, Loader2, Send } from "lucide-react";

const MOODS = [
  { key: "sad",     emoji: "😞", label: "Frustrated" },
  { key: "neutral", emoji: "😐", label: "Okay" },
  { key: "happy",   emoji: "🙂", label: "Great" },
  { key: "loving",  emoji: "😍", label: "Love it" },
];

export default function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mood, setMood] = useState("happy");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setMood("happy"); setMessage(""); };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feedback", { mood, message, page: location.pathname });
      toast.success("Thanks — your feedback helps us improve Adyap 🙌");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black font-medium px-4 py-3 rounded-full shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5"
        aria-label="Share feedback"
      >
        <MessageCircleHeart className="w-4 h-4" />
        <span className="hidden sm:inline text-sm">Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div
            className="w-full sm:max-w-md glass rounded-2xl p-6 border border-white/10"
            style={{ background: "rgba(15,15,15,0.95)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-modal"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-xl font-extrabold tracking-tighter">How are you feeling about Adyap?</h3>
                <p className="text-sm text-zinc-400 mt-1">Tell us anything — bugs, wishlist, or love notes. We read every single one.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-4 gap-2" data-testid="mood-picker">
              {MOODS.map((m) => (
                <button
                  key={m.key}
                  data-testid={`mood-${m.key}`}
                  onClick={() => setMood(m.key)}
                  className={`flex flex-col items-center gap-1 py-3 rounded-xl border transition-all ${mood === m.key ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/20"}`}
                  type="button"
                >
                  <span className="text-2xl">{m.emoji}</span>
                  <span className="text-[10px] tracking-wider uppercase font-semibold text-zinc-400">{m.label}</span>
                </button>
              ))}
            </div>

            <label className="block mt-5">
              <span className="text-xs tracking-wider uppercase font-semibold text-zinc-500">What could we improve?</span>
              <textarea
                data-testid="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Optional — features you'd love, things that felt confusing, or what's working well."
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                maxLength={2000}
              />
            </label>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors">
                Cancel
              </button>
              <button
                data-testid="feedback-submit"
                onClick={submit}
                disabled={busy}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black px-5 py-2.5 rounded-xl font-medium disabled:opacity-60 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
